/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import { Stack, Checkbox, Tooltip } from '@airbus/components-react';
import { FixedSizeList as List } from 'react-window';
import './AdvanceComboBox.scss';
import AdvanceComboBoxRow from './AdvanceComboBoxRow';

// Props for AdvanceComboBoxList component
interface AdvanceComboBoxListProps {
  handleOptionChange: (option: AcbObjectType) => void;
  listHeight: number;
  rowHeight: number;
  virtualizationThreshold: number;
  filterOptions: Array<any>;
}

/**
 * AdvanceComboBoxList
 * -----------------------------------------------------------------------------
 * Renders the list of options for the AdvanceComboBox dropdown.
 *
 * Features:
 * - Displays a list of selectable options, each as a checkbox.
 * - Uses react-window virtualization for efficient rendering of large lists.
 * - Shows a "No options" message if the list is empty.
 * - Delegates row rendering to AdvanceComboBoxRow when virtualized.
 *
 * Props:
 * - filteredOptions: Array of options to display.
 * - selectedOptions: Array of currently selected options.
 * - handleOptionChange: Handler for selecting/deselecting an option.
 * - listHeight: Height of the options list container.
 * - rowHeight: Height of each row in the list.
 * - virtualizationThreshold: Minimum number of options to enable virtualization.
 *
 * This component is used internally by AdvanceComboBoxContent to render the options list.
 */
const AdvanceComboBoxList: React.FC<AdvanceComboBoxListProps> = ({
  handleOptionChange,
  listHeight,
  rowHeight,
  virtualizationThreshold,
  filterOptions,
}) => {
  // Show a message if there are no options to display
  if (filterOptions.length === 0) {
    return <div className="no-options">No options</div>;
  }

  // Use react-window virtualization if the number of options exceeds the threshold
  if (filterOptions.length > virtualizationThreshold) {
    return (
      <List
        height={listHeight}
        itemCount={filterOptions.length}
        itemSize={rowHeight}
        width="100%"
      >
        {({ index, style }) => (
          // Render each row using a custom row component for virtualization
          <AdvanceComboBoxRow
            index={index}
            style={style}
            filteredOptions={filterOptions}
            handleOptionChange={handleOptionChange}
          />
        )}
      </List>
    );
  }

  // Render all options as checkboxes if virtualization is not needed
  return (
    <>
      {filterOptions.map((option) => (
        <Stack key={option.name} spacing="1-x">
          <Tooltip label={option?.name}>
            <Checkbox
              data-testid={`ACB-option-${option.name}`}
              className="advance-combo-checkbox"
              label={option.name}
              checked={!!option.checked}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={() => {
                handleOptionChange(option);
              }}
              disabled={false}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </Tooltip>
        </Stack>
      ))}
    </>
  );
};

export default AdvanceComboBoxList;
